import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
    Box,
    Typography,
    Grid,
    IconButton,
    Divider,
    alpha,
    useTheme,
    CircularProgress,
    Menu,
    MenuItem,
    MenuItem as SelectMenuItem,
    Tabs,
    Tab,
    Card,
    CardContent,
    Button,
    TextField,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Select,
    FormControl,
    InputLabel,
    Stack,
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    Add as AddIcon,
    Search as SearchIcon,
} from '@mui/icons-material';
import { leadApi } from '../services/api';
import { auth } from '../firebase';
import PageAlert from './PageAlert';
import LeadCard from './crm/LeadCard';
import { STATUS_CONFIG, CRM_TABS } from './crm/CRMConstants';
import ConnectPlatformsCTA from './ConnectPlatformsCTA';
import { io } from 'socket.io-client';

const LeadCRM = () => {
    const theme = useTheme();

    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('new');
    const hasAutoFetched = useRef(false);

    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedLead, setSelectedLead] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [newLead, setNewLead] = useState({
        username: '',
        comment_text: '',
        platform: 'instagram',
        status: 'new',
        is_high_value: false
    });

    // Unified error extraction
    const errorMsg = typeof error === 'string' ? error : (error?.message || '');
    const isNoAccountsError = !!errorMsg.toLowerCase().match(/no .*accounts|link.*platforms|social.*media.*setup/i);

    const fetchLeads = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await leadApi.getAll();
            setLeads(data || []);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!hasAutoFetched.current) {
            hasAutoFetched.current = true;
            fetchLeads();
        }
    }, [fetchLeads]);

    // Socket.io for real-time new lead sync
    useEffect(() => {
        const socket = io(process.env.REACT_APP_API_URL || 'http://127.0.0.1:5001');

        socket.on('connect', () => {
            const uid = auth.currentUser?.uid;
            if (uid) {
                socket.emit('authenticate', { uid });
            }
        });

        socket.on('new_lead', (data) => {
            // New lead created via automation
            console.log('[LeadCRM] new_lead event via socket', data);
            fetchLeads();
        });

        return () => {
            socket.disconnect();
        };
    }, [fetchLeads]);

    const handleMenuOpen = (event, lead) => {
        setAnchorEl(event.currentTarget);
        setSelectedLead(lead);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedLead(null);
    };

    const handleStatusUpdate = async (status) => {
        if (!selectedLead) return;

        let updateData = { status };

        if (status === 'won') {
            const val = window.prompt('Enter deal value (e.g. 500):', '0');
            if (val !== null) {
                updateData.deal_value = parseFloat(val) || 0;
                updateData.convertedAt = new Date().toISOString();
            } else {
                return; // Cancelled
            }
        }

        try {
            await leadApi.update(selectedLead.id, updateData);
            handleMenuClose();
            fetchLeads(); // Refresh leads
        } catch (err) {
            setError('Failed to update status');
        }
    };

    const handleDelete = async () => {
        if (!selectedLead) return;
        if (window.confirm('Are you sure you want to delete this lead?')) {
            try {
                await leadApi.delete(selectedLead.id);
                handleMenuClose();
                fetchLeads(); // Refresh leads
            } catch (err) {
                setError('Failed to delete lead');
            }
        }
    };

    const handleCreateLead = async () => {
        try {
            await leadApi.create(newLead);
            setOpenAddDialog(false);
            setNewLead({
                username: '',
                comment_text: '',
                platform: 'instagram',
                status: 'new',
                is_high_value: false
            });
            fetchLeads();
        } catch (err) {
            setError('Failed to create lead');
        }
    };

    const conversionStats = useMemo(() => {
        const total = leads.length;
        const won = leads.filter(l => l.status === 'won');
        const lost = leads.filter(l => l.status === 'lost').length;
        const revenue = won.reduce((sum, l) => sum + (parseFloat(l.deal_value) || 0), 0);
        return {
            total,
            won: won.length,
            lost,
            active: total - won.length - lost,
            conversionRate: total > 0 ? Math.round((won.length / total) * 100) : 0,
            revenue,
        };
    }, [leads]);

    if (isNoAccountsError) {
        return (
            <Box sx={{ py: 6 }}>
                <ConnectPlatformsCTA
                    title="Lead CRM Locked"
                    description="Connect your social media platforms to start tracking potential customers and managing your relationships."
                    buttonText="Link Your Platforms"
                />
            </Box>
        );
    }

    const filteredLeads = leads.filter(lead => {
        const query = searchQuery.toLowerCase();
        return (
            (lead.comment_text || '').toLowerCase().includes(query) ||
            (lead.username || '').toLowerCase().includes(query)
        );
    });

    const leadsByStatus = (status) => filteredLeads.filter(l => l.status === status);

    const renderEmptyState = () => (
        <Box sx={{
            py: 12,
            textAlign: 'center',
            border: `2px dashed ${alpha(theme.palette.divider, 0.1)}`,
            borderRadius: 8,
            bgcolor: alpha(theme.palette.background.paper, 0.4),
            width: '100%'
        }}>
            <Typography variant="h6" sx={{ color: 'text.disabled', fontWeight: 700, mb: 1 }}>
                No leads here
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                Potential customers detected by AI will appear in this stage.
            </Typography>
        </Box>
    );

    return (
        <Box>
            <Box sx={{ mb: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                    <Typography variant="h3" sx={{ fontWeight: 900, mb: 1 }}>
                        Lead Management
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        Track and manage your potential customers in real-time.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                    <IconButton onClick={fetchLeads} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main' }}>
                        <RefreshIcon />
                    </IconButton>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setOpenAddDialog(true)}
                        sx={{
                            borderRadius: 3,
                            fontWeight: 700,
                            background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                            boxShadow: '0 8px 16px -4px rgba(99, 102, 241, 0.3)',
                        }}
                    >
                        New Lead
                    </Button>
                </Stack>
            </Box>

            <Box sx={{ mb: 4 }}>
                <TextField
                    fullWidth
                    placeholder="Search by username or comment text..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    variant="outlined"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: 'text.disabled' }} />
                            </InputAdornment>
                        ),
                        sx: {
                            borderRadius: 4,
                            bgcolor: alpha(theme.palette.background.paper, 0.8),
                            '& fieldset': { border: 'none' },
                            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                        }
                    }}
                />
            </Box>

            {/* Conversion Stats Bar */}
            {!loading && leads.length > 0 && (
                <Grid container spacing={2} sx={{ mb: 4 }}>
                    {[
                        { label: 'Total Leads', value: conversionStats.total, color: theme.palette.primary.main },
                        { label: 'Won', value: conversionStats.won, color: theme.palette.success.main },
                        { label: 'Conversion Rate', value: `${conversionStats.conversionRate}%`, color: theme.palette.info.main },
                        { label: 'Revenue', value: `$${conversionStats.revenue.toLocaleString()}`, color: theme.palette.secondary.main },
                    ].map(({ label, value, color }) => (
                        <Grid xs={6} sm={3} key={label}>
                            <Box sx={{
                                p: 2.5,
                                borderRadius: 3,
                                bgcolor: alpha(color, 0.08),
                                border: `1px solid ${alpha(color, 0.15)}`,
                                textAlign: 'center',
                            }}>
                                <Typography variant="h5" sx={{ fontWeight: 900, color, lineHeight: 1 }}>
                                    {value}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mt: 0.5, display: 'block' }}>
                                    {label}
                                </Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            )}

            <PageAlert error={error} />

            {/* ── Conversion Stats Bar ─────────────────────────────────── */}
            {!loading && leads.length > 0 && (
                <Grid container spacing={2} sx={{ mb: 4 }}>
                    {[
                        {
                            label: 'Total Leads',
                            value: conversionStats.total,
                            color: theme.palette.text.primary,
                            bg: alpha(theme.palette.text.primary, 0.05),
                        },
                        {
                            label: 'Won',
                            value: conversionStats.won,
                            color: theme.palette.success.main,
                            bg: alpha(theme.palette.success.main, 0.08),
                        },
                        {
                            label: 'Conversion Rate',
                            value: `${conversionStats.conversionRate}%`,
                            color: theme.palette.primary.main,
                            bg: alpha(theme.palette.primary.main, 0.08),
                        },
                        {
                            label: 'Revenue',
                            value: conversionStats.revenue > 0
                                ? `$${conversionStats.revenue.toLocaleString()}`
                                : '—',
                            color: theme.palette.secondary.main,
                            bg: alpha(theme.palette.secondary.main, 0.08),
                        },
                    ].map((stat) => (
                        <Grid xs={6} md={3} key={stat.label}>
                            <Card sx={{
                                borderRadius: 3,
                                border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                                bgcolor: stat.bg,
                                boxShadow: 'none',
                            }}>
                                <CardContent sx={{ py: 2, px: 3, '&:last-child': { pb: 2 } }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        {stat.label}
                                    </Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 900, color: stat.color, mt: 0.5 }}>
                                        {stat.value}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Box>
                    <Tabs
                        value={activeTab}
                        onChange={(_, v) => setActiveTab(v)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            mb: 5,
                            '& .MuiTabs-indicator': { height: 4, borderRadius: '4px 4px 0 0' },
                            '& .MuiTab-root': {
                                fontWeight: 800,
                                fontSize: '1rem',
                                textTransform: 'none',
                                minWidth: 140,
                                py: 2,
                                transition: 'all 0.2s',
                                '&.Mui-selected': {
                                    color: 'primary.main',
                                }
                            }
                        }}
                    >
                        {CRM_TABS.map(tab => (
                            <Tab
                                key={tab.value}
                                value={tab.value}
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {tab.label}
                                        <Box sx={{
                                            px: 1,
                                            py: 0.25,
                                            borderRadius: 1.5,
                                            bgcolor: activeTab === tab.value ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.text.disabled, 0.1),
                                            fontSize: '0.75rem',
                                            fontWeight: 900
                                        }}>
                                            {leadsByStatus(tab.value).length}
                                        </Box>
                                    </Box>
                                }
                            />
                        ))}
                    </Tabs>

                    <Box sx={{ minHeight: 400 }}>
                        <Grid container spacing={3}>
                            {leadsByStatus(activeTab).map(lead => (
                                <Grid xs={12} sm={6} lg={4} key={lead.id}>
                                    <LeadCard lead={lead} onMenuOpen={handleMenuOpen} />
                                </Grid>
                            ))}
                            {leadsByStatus(activeTab).length === 0 && renderEmptyState()}
                        </Grid>
                    </Box>
                </Box>
            )}

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                        minWidth: 200,
                        boxShadow: `0 20px 40px ${alpha(theme.palette.common.black, 0.1)}`,
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        p: 1
                    }
                }}
            >
                <Typography variant="caption" sx={{ px: 2, py: 1, display: 'block', fontWeight: 900, color: 'text.disabled', textTransform: 'uppercase' }}>
                    Move to Stage
                </Typography>
                {CRM_TABS.map((tab) => (
                    <MenuItem
                        key={tab.value}
                        onClick={() => handleStatusUpdate(tab.value)}
                        sx={{ borderRadius: 2, mb: 0.5, fontWeight: 700 }}
                    >
                        <Box sx={{ mr: 1.5, color: `${STATUS_CONFIG[tab.value].color}.main`, display: 'flex' }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'currentColor' }} />
                        </Box>
                        {tab.label}
                    </MenuItem>
                ))}
                <Divider sx={{ my: 1 }} />
                <MenuItem onClick={handleDelete} sx={{ borderRadius: 2, color: 'error.main', fontWeight: 700 }}>
                    <DeleteIcon sx={{ mr: 1.5 }} fontSize="small" /> Delete Lead
                </MenuItem>
            </Menu>

            <Dialog
                open={openAddDialog}
                onClose={() => setOpenAddDialog(false)}
                PaperProps={{ sx: { borderRadius: 4, p: 1, minWidth: 400 } }}
            >
                <DialogTitle sx={{ fontWeight: 900 }}>Create Manual Lead</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <TextField
                            label="Username"
                            fullWidth
                            value={newLead.username}
                            onChange={(e) => setNewLead({ ...newLead, username: e.target.value })}
                            placeholder="e.g. johndoe"
                        />
                        <TextField
                            label="Comment Text"
                            fullWidth
                            multiline
                            rows={3}
                            value={newLead.comment_text}
                            onChange={(e) => setNewLead({ ...newLead, comment_text: e.target.value })}
                            placeholder="What was the inquiry?"
                        />
                        <FormControl fullWidth>
                            <InputLabel>Platform</InputLabel>
                            <Select
                                value={newLead.platform}
                                label="Platform"
                                onChange={(e) => setNewLead({ ...newLead, platform: e.target.value })}
                            >
                                <SelectMenuItem value="instagram">Instagram</SelectMenuItem>
                                <SelectMenuItem value="youtube">YouTube</SelectMenuItem>
                                <SelectMenuItem value="twitter">X (Twitter)</SelectMenuItem>
                                <SelectMenuItem value="facebook">Facebook</SelectMenuItem>
                                <SelectMenuItem value="manual">Manual Entry</SelectMenuItem>
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel>Initial Status</InputLabel>
                            <Select
                                value={newLead.status}
                                label="Initial Status"
                                onChange={(e) => setNewLead({ ...newLead, status: e.target.value })}
                            >
                                {CRM_TABS.map(tab => (
                                    <SelectMenuItem key={tab.value} value={tab.value}>{tab.label}</SelectMenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setOpenAddDialog(false)} color="inherit" sx={{ fontWeight: 700 }}>Cancel</Button>
                    <Button
                        onClick={handleCreateLead}
                        variant="contained"
                        disabled={!newLead.comment_text}
                        sx={{ borderRadius: 3, fontWeight: 700 }}
                    >
                        Create Lead
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default LeadCRM;
